/**
 * Custom CKEditor Plugin for Font Attribute Commands
 * 
 * This module provides a command implementation for handling custom font attributes in CKEditor.
 * It allows dynamic application, removal, and management of font-related attributes on the editor's content.
 * 
 * ## Key Features
 * - **Dynamic Attribute Handling**: Supports adding and removing custom font attributes (e.g., color, background color).
 * - **Selection-Aware**: Automatically applies attributes to the current selection or removes them when the selection is collapsed.
 * - **Batch Changes**: Enables undoable, batch-style changes for seamless user experience.
 * - **Integration with Editor Schema**: Validates and manages attributes within the constraints of the editor's schema.
 * 
 * ### Core Components
 * - **CustomFontCommand**: A generic command to handle font-related attributes.
 *   - Automatically tracks the value and enables state of the custom attribute.
 *   - Applies or removes the attribute based on user input and selection.
 * - **CustomFontColorCommand**: Extends the generic command to specifically handle font color changes.
 * 
 * ### Events
 * - **State Refresh**: Updates command value and state when the editor's selection changes.
 * - **Attribute Updates**: Dynamically applies or removes attributes based on the editor's selection and user input.
 * 
 * ### References
 * - CKEditor Command API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_core_command-Command.html
 * - CKEditor Schema API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_model_schema-Schema.html
 * 
 * @module CustomFontCommand
 * @exports CustomFontCommand, CustomFontColorCommand
 */

import { CKPlugins } from '../../constants/constants';

const { Command } = window.ClassicEditor;

export class CustomFontCommand extends Command {
	value;
    attributeKey;
    editor;
    isEnabled;

	constructor(editor, attributeKey) {
		super(editor);

		this.attributeKey = attributeKey;
	}

	refresh() {
		const model = this.editor.model;
		const doc = model.document;

		this.value = doc.selection.getAttribute(this.attributeKey);
		this.isEnabled = model.schema.checkAttributeInSelection(doc.selection, this.attributeKey);
	}

	execute(options: any = {}) {
		const model = this.editor.model;
		const document = model.document;
		const selection = document.selection;
		const value = options.value;
		const batch = options.batch;

		const updateAttribute = writer => {
			if (selection.isCollapsed) {
				if (value) {
					writer.setSelectionAttribute(this.attributeKey, value);
				} else {
					writer.removeSelectionAttribute(this.attributeKey);
				}
			} else {
				const ranges = model.schema.getValidRanges(selection.getRanges(), this.attributeKey);
				for (const range of ranges) {
					if ( value ) {
						writer.setAttribute(this.attributeKey, value, range);
					} else {
						writer.removeAttribute(this.attributeKey, range);
					}
				}
			}
		};

		// In some scenarios, you may want to use a single undo step for multiple changes (e.g. in color picker).
		if (batch) {
			model.enqueueChange( batch, writer => {
				updateAttribute( writer );
			} );
		} else {
			model.change(writer => {
				updateAttribute(writer);
			});
		}
	}
}

export class CustomFontColorCommand extends CustomFontCommand {
    constructor(editor) {
		super(editor, CKPlugins.CustomFontColor);
	}
}
